import { api } from '../../../lib/api';
import type { AdminOverview } from '../adminApi';

/**
 * Il contratto della Situazione: `GET /admin/situazione`
 * (apps/api/src/modules/admin/admin.situazione.ts). Lo leggono la pagina
 * Situazione e la home (Oggi, blocco "Come stiamo con i soldi"): stessa cache,
 * stessi numeri.
 *
 * Qui non si fanno conti: la frase, i totali, i mesi e i problemi arrivano
 * gia' fatti dal server, lo stesso che scrive la mail del lunedi'.
 */

export interface StatoSync {
  provider: 'QONTO' | 'FIC';
  nome: string;
  collegato: boolean;
  ultimoOk: string | null;
  ultimoTentativo: string | null;
  /** INTERROTTO = il server si è fermato a metà giro: non è un errore del fornitore. */
  esito: 'OK' | 'FAILED' | 'RUNNING' | 'INTERROTTO' | null;
  errore: string | null;
  /** Falliti davvero (errori di rete compresi); gli interrotti non ci sono. */
  fallitiUltime24h: number;
  senzaReteUltime24h: number;
  interrottiUltime24h: number;
  erroreUltime24h: string | null;
  problema: boolean;
  testo: string;
  testoProblema: string | null;
}

export type ParteScaduti = 'fisco' | 'fornitori' | 'speseFisse' | 'stipendi' | 'contanti';

export interface Scaduti {
  totale: number;
  fisco: { importo: number; rate: number; provvisorio: number; iva: { importo: number; trimestri: number; dal: number | null } };
  fornitori: { importo: number; documenti: number };
  speseFisse: { importo: number; occorrenze: number };
  stipendi: { importo: number; occorrenze: number };
  contanti: { importo: number; voci: number };
  descrizione: string;
  principale: ParteScaduti | null;
}

export interface VoceMese { nome: string; importo: number; stima: boolean }
export interface ParteMese {
  chiave: 'fatture' | 'canoni' | 'contanti' | 'spese' | 'stipendi' | 'fisco';
  etichetta: string;
  importo: number;
  voci: VoceMese[];
  altre: number;
}
export interface MesePrevisione {
  mese: string;
  nome: string;
  dal: string | null;
  entra: { totale: number; parti: ParteMese[] };
  esce: { totale: number; parti: ParteMese[]; stima: number };
  saldoFine: number;
  piuStretto: boolean;
}
export interface Previsione {
  oggi: string;
  da: string;
  a: string;
  partenza: { banca: number; contanti: number; totale: number };
  inizioFinestra: number;
  mesi: MesePrevisione[];
  piuStretto: string | null;
  sottoZero: string | null;
}

export interface Problema {
  tipo: 'MESE_SOTTO_ZERO' | 'TASSE_ENTRO_7' | 'SYNC' | 'FATTURE_OLTRE_60';
  testo: string;
  dettaglio: string | null;
  link: string;
  provider?: 'QONTO' | 'FIC';
}

export interface Situazione {
  oggi: string;
  frase: string;
  disponibili: { totale: number; banca: number; contanti: number; bancaAggiornataAl: string | null; bancaAggiornataTesto: string };
  clienti: {
    totale: number;
    fattureAperte: number;
    fattureInRitardo: number;
    oltre60: Array<{ numero: string | null; cliente: string; giorni: number; tempo: string }>;
  };
  scaduti: Scaduti;
  previsione: Previsione;
  sync: { qonto: StatoSync; fic: StatoSync };
  personale: { persone: number; senzaStipendio: string[] };
  problemi: Problema[];
}

/** La finestra dei mesi: "i prossimi N" oppure "da un mese a un altro" (YYYY-MM). */
export type Finestra = { mesi: number } | { da: string; a: string };

export function situazioneQuery(f: Finestra = { mesi: 3 }) {
  const sp = new URLSearchParams();
  if ('mesi' in f) sp.set('mesi', String(f.mesi));
  else { sp.set('da', f.da); sp.set('a', f.a); }
  const qs = sp.toString();
  return {
    queryKey: ['admin', 'situazione', qs] as const,
    queryFn: () => api.get<Situazione>(`/admin/situazione?${qs}`),
    // Stesso ritmo dell'overview: e' la stessa cassa.
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  };
}

// ── Campi dell'overview arrivati con il lotto 4 ─────────────────────────────
// adminApi.ts (fuori da questo lotto) non li dichiara ancora: si leggono da qui.

export interface AbbinamentoSicuroUscita {
  rataId: string;
  docId: string;
  docNumero: string | null;
  controparte: string;
  importo: number;
  dueDate: string | null;
  movimento: { id: string; settledOn: string | null; label: string; counterparty: string | null };
}
export interface CifraMancante {
  installmentId: string;
  obligationId: string;
  kind: string;
  description: string;
  dueDate: string | null;
  overdueDays: number;
}
/** Un canone con la scadenza passata e senza la fattura del mese (admin.service.ts, canoniDaFatturare). */
export interface CanoneDaFatturare {
  recurringRevenueId: string;
  clientId: string;
  projectId: string | null;
  /** "Emmequadro": dal nome del canone, altrimenti il nome del cliente. */
  cliente: string;
  /** YYYY-MM dell'occorrenza. */
  mese: string;
  dueDate: string;
  importo: number;
}
/** Stipendi e TFR senza bonifico, da verificare in Dipendenti: solo conteggi e nomi, mai l'importo (R2). */
export interface VersamentiDaVerificare { count: number; stipendi: number; tfr: number; persone: string[] }
export interface OverviewLotto4 {
  scaduti?: Scaduti;
  daFare?: {
    abbinamentiSicuriUscite: AbbinamentoSicuroUscita[];
    cifreMancanti: CifraMancante[];
    canoniDaFatturare?: CanoneDaFatturare[];
    /** `null` per chi non può aprire Dipendenti. */
    versamentiDaVerificare?: VersamentiDaVerificare | null;
  };
  sync?: { qonto?: Partial<StatoSync>; fic?: Partial<StatoSync> };
}
export function lotto4(ov: AdminOverview | undefined): OverviewLotto4 {
  return (ov ?? {}) as unknown as OverviewLotto4;
}

// ── Parole ──────────────────────────────────────────────────────────────────

function durata(giorni: number): string {
  if (giorni < 14) return giorni === 1 ? 'un giorno' : `${giorni} giorni`;
  if (giorni < 60) return `${Math.floor(giorni / 7)} settimane`;
  if (giorni < 365) { const m = Math.floor(giorni / 30); return m === 1 ? 'un mese' : `${m} mesi`; }
  const a = Math.floor(giorni / 365);
  return a === 1 ? 'più di un anno' : `${a} anni`;
}

/**
 * Il tempo in parole rispetto a oggi, come lo scrive il server: "oggi",
 * "domani", "tra 6 giorni", "da ieri", "da 6 mesi". La data esatta va nel
 * tooltip. `giorni` > 0 = nel futuro, < 0 = nel passato.
 */
export function tempoInParole(giorni: number): string {
  if (giorni === 0) return 'oggi';
  if (giorni === 1) return 'domani';
  if (giorni === -1) return 'da ieri';
  return giorni > 0 ? `tra ${durata(giorni)}` : `da ${durata(-giorni)}`;
}

/** "Scade tra 6 giorni", "In ritardo da 6 mesi": la scadenza di una rata in parole. */
export function scadenzaInParole(giorni: number | null, scaduta = 'In ritardo'): string {
  if (giorni == null) return 'Senza scadenza';
  if (giorni < 0) return `${scaduta} ${tempoInParole(giorni)}`;
  if (giorni === 0) return 'Scade oggi';
  if (giorni === 1) return 'Scade domani';
  return `Scade ${tempoInParole(giorni)}`;
}

/** "10:45" o "24/09/2026 10:45": l'ora esatta per i tooltip. */
export function orarioEsatto(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
